import React from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ForcePasswordChangeDialog } from "@/components/ForcePasswordChangeDialog";

// `/` and `/inventory` both render the marketplace browse grid. The
// Phase 10 Home tools-hero was rolled back per user; Home is now a thin
// alias over Inventory.
import Home from "@/pages/Home";
import Inventory from "@/pages/Inventory";
import VehicleDetail from "@/pages/VehicleDetail";
import Dealers from "@/pages/Dealers";
import DealerProfile from "@/pages/DealerProfile";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import SavedVehicles from "@/pages/SavedVehicles";
import MyLeads from "@/pages/MyLeads";
import FinanceQuotes from "@/pages/FinanceQuotes";
import TradeIns from "@/pages/TradeIns";

// Phase 2 dealer portal
import DealerLayout from "@/pages/dealer/DealerLayout";
import DealerDashboard from "@/pages/dealer/DealerDashboard";
import DealerInventoryPage from "@/pages/dealer/DealerInventory";
import DealerLeadsPage from "@/pages/dealer/DealerLeads";
import DealerAppointmentsPage from "@/pages/dealer/DealerAppointments";
import DealerAnalyticsPage from "@/pages/dealer/DealerAnalytics";
import DealerProfileEditPage from "@/pages/dealer/DealerProfileEdit";
import DealerBillingPage from "@/pages/dealer/DealerBilling";

// Phase 3 BDC portal
import BdcLayout from "@/pages/bdc/BdcLayout";
import BdcDashboard from "@/pages/bdc/BdcDashboard";
import BdcQueue from "@/pages/bdc/BdcQueue";
import BdcLeadWorkspace from "@/pages/bdc/BdcLeadWorkspace";
import BdcChat from "@/pages/bdc/BdcChat";
import BdcAppointments from "@/pages/bdc/BdcAppointments";
import BdcPhone from "@/pages/bdc/BdcPhone";

// Phase 4 Admin portal
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminLeadDetail from "@/pages/admin/AdminLeadDetail";
import AdminDealers from "@/pages/admin/AdminDealers";
import AdminDealerDetail from "@/pages/admin/AdminDealerDetail";
import AdminBdc from "@/pages/admin/AdminBdc";
import AdminUsers from "@/pages/admin/AdminUsers";
import BdcManagerTeam from "@/pages/bdc/BdcManagerTeam";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminPhone from "@/pages/admin/AdminPhone";
import AdminToolsInsights from "@/pages/admin/AdminToolsInsights";
import AdminCommissions from "@/pages/admin/AdminCommissions";
import AdminInventory from "@/pages/admin/AdminInventory";
import BdcTeam from "@/pages/bdc/BdcTeam";
import BdcSettingsPayment from "@/pages/bdc/BdcSettingsPayment";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function AppRoutes() {
  const location = useLocation();
  // If we've come back from Emergent OAuth, process the session_id first.
  if (location.hash && location.hash.includes("session_id=")) {
    return (
      <Layout>
        <AuthCallback />
      </Layout>
    );
  }

  return (
    <>
      <ImpersonationBanner />
      <Routes>
      <Route
        path="/"
        element={
          <Layout>
            <Home />
          </Layout>
        }
      />
      <Route
        path="/inventory"
        element={
          <Layout>
            <Inventory />
          </Layout>
        }
      />
      <Route
        path="/vehicle/:id"
        element={
          <Layout>
            <VehicleDetail />
          </Layout>
        }
      />
      <Route
        path="/dealers"
        element={
          <Layout>
            <Dealers />
          </Layout>
        }
      />
      <Route
        path="/dealers/:id"
        element={
          <Layout>
            <DealerProfile />
          </Layout>
        }
      />
      <Route
        path="/login"
        element={
          <Layout>
            <Login />
          </Layout>
        }
      />
      <Route
        path="/signup"
        element={
          <Layout>
            <Signup />
          </Layout>
        }
      />
      <Route
        path="/auth/callback"
        element={
          <Layout>
            <AuthCallback />
          </Layout>
        }
      />

      {/* Customer-only */}
      <Route
        path="/account"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <Account />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/saved"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <SavedVehicles />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/leads"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <MyLeads />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/finance-quotes"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <FinanceQuotes />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/trade-ins"
        element={
          <ProtectedRoute roles={["customer"]}>
            <Layout>
              <TradeIns />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Dealer portal (Phase 2) — nested routes with a dedicated shell */}
      <Route
        path="/dealer"
        element={
          <ProtectedRoute roles={["dealer"]}>
            <DealerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DealerDashboard />} />
        <Route path="inventory" element={<DealerInventoryPage />} />
        <Route path="leads" element={<DealerLeadsPage />} />
        <Route path="appointments" element={<DealerAppointmentsPage />} />
        <Route path="analytics" element={<DealerAnalyticsPage />} />
        <Route path="billing" element={<DealerBillingPage />} />
        <Route path="profile" element={<DealerProfileEditPage />} />
      </Route>

      {/* BDC portal (Phase 3) — nested routes with a dedicated shell */}
      <Route
        path="/bdc"
        element={
          <ProtectedRoute roles={["bdc", "bdc_manager"]}>
            <BdcLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<BdcDashboard />} />
        <Route path="queue" element={<BdcQueue />} />
        <Route path="leads/:id" element={<BdcLeadWorkspace />} />
        <Route path="chat" element={<BdcChat />} />
        <Route path="phone" element={<BdcPhone />} />
        <Route path="appointments" element={<BdcAppointments />} />
        <Route path="team" element={<BdcTeam />} />
        <Route path="manager/team" element={<BdcManagerTeam />} />
        <Route path="settings/payment" element={<BdcSettingsPayment />} />
      </Route>

      {/* Admin portal (Phase 4) — nested routes with a dedicated shell */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="customers/:id" element={<AdminCustomerDetail />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="leads/:id" element={<AdminLeadDetail />} />
        <Route path="dealers" element={<AdminDealers />} />
        <Route path="dealers/:id" element={<AdminDealerDetail />} />
        <Route path="bdc" element={<AdminBdc />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="phone" element={<AdminPhone />} />
        <Route path="analytics/commissions" element={<AdminCommissions />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
      </Route>

      <Route
        path="*"
        element={
          <Layout>
            <div className="max-w-3xl mx-auto text-center py-24">
              <p className="font-display text-3xl text-slate-800">Page not found</p>
              <p className="mt-2 text-sm text-slate-500">
                The page you're looking for doesn't exist.
              </p>
            </div>
          </Layout>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        {/* Global force-password-change dialog — intercepts any authed user
            with `force_password_change=true` regardless of which portal they
            landed in. Non-dismissible until a new password is set. */}
        <ForcePasswordChangeDialog />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
